import {
	authClient,
	signInDiscord,
	signInDoras,
	signInEmail,
	signInEmailTwoFactor,
	signInGithub,
	signInSlack,
} from "@repo/auth/client";
import { Badge } from "@repo/ui/components/badge";
import TasqIcon from "@repo/ui/components/brand-icon";
import { Button } from "@repo/ui/components/button";
import { Dialog, DialogContent, DialogTrigger } from "@repo/ui/components/dialog";
import { headlessToast } from "@repo/ui/components/headless-toast";
import { Input } from "@repo/ui/components/input";
import { Separator } from "@repo/ui/components/separator";
import { cn } from "@repo/ui/lib/utils";
import { IconBrandDiscordFilled, IconBrandGithubFilled, IconBrandSlack, IconFingerprint } from "@tabler/icons-react";
import { createServerFn } from "@tanstack/react-start";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { authErrorCode } from "@/lib/auth/reauth";

interface OAuthProviders {
	github: boolean;
	doras: boolean;
	discord: boolean;
	slack: boolean;
}

const getOAuthProviders = createServerFn({ method: "GET" }).handler(async (): Promise<OAuthProviders> => {
	return {
		github: !!(process.env.GITHUB_CLIENT_ID && process.env.GITHUB_CLIENT_SECRET),
		doras: !!(process.env.DORAS_CLIENT_ID && process.env.DORAS_CLIENT_SECRET),
		discord: !!(process.env.DISCORD_CLIENT_ID && process.env.DISCORD_CLIENT_SECRET),
		slack: !!(process.env.SLACK_CLIENT_ID && process.env.SLACK_CLIENT_SECRET),
	};
});

interface Props {
	trigger?: React.ReactElement;
}

export default function LoginDialog({ trigger }: Props) {
	const [providers, setProviders] = useState<OAuthProviders | undefined>(undefined);

	useEffect(() => {
		getOAuthProviders().then(setProviders);
	}, []);

	return (
		<Dialog>
			<DialogTrigger render={trigger || <Button variant="outline">Sign in</Button>} />
			<DialogContent className="sm:max-w-sm p-0 gap-0 overflow-hidden rounded-2xl!">
				<LoginComponent isDialog providers={providers} />
			</DialogContent>
		</Dialog>
	);
}

export function LoginComponent({ isDialog = false, providers }: { isDialog?: boolean; providers?: OAuthProviders }) {
	const [lastMethod, setLastMethod] = useState<string | null>(null);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [name, setName] = useState("");
	const [step, setStep] = useState<"email" | "password" | "name">("email");
	const [isSignUp, setIsSignUp] = useState(false);
	useEffect(() => {
		setLastMethod(authClient.getLastUsedLoginMethod());
	}, []);
	// Passkey autofill: a saved passkey shows up as a suggestion in the email field's autofill list (the input's
	// `webauthn` autocomplete token). `autoFill: true` keeps this passive; without it the browser opens its passkey
	// prompt on every visit, for everyone.
	useEffect(() => {
		void (async () => {
			if (
				typeof PublicKeyCredential === "undefined" ||
				typeof PublicKeyCredential.isConditionalMediationAvailable !== "function" ||
				!(await PublicKeyCredential.isConditionalMediationAvailable())
			) {
				return;
			}
			const result = await authClient.signIn.passkey({ autoFill: true });
			if (result.data?.session) {
				await signInEmail();
			}
		})();
	}, []);

	// Signing in with a passkey only finds an existing account; it can't create one (registering a passkey needs a
	// signed-in session), so a passkey with no account behind it just gets an explanation.
	const handlePasskeySignIn = async () => {
		const result = await authClient.signIn.passkey();
		if (result.data?.session) {
			await signInEmail();
			return;
		}
		const code = authErrorCode(result.error);
		// Closing the browser's passkey prompt is not an error worth a toast.
		if (!code || code === "AUTH_CANCELLED" || code.startsWith("ERROR_")) return;
		headlessToast.error({
			title: "Couldn't sign in with a passkey",
			description:
				code === "PASSKEY_NOT_FOUND"
					? "No account uses that passkey. Sign in another way, then add a passkey under Settings → Security."
					: result.error?.message || "Please try again or sign in another way.",
		});
	};
	const handleEmailSubmit = () => {
		if (email) {
			if (isSignUp) {
				setStep("name");
			} else {
				setStep("password");
			}
		}
	};

	const handlePasswordSubmit = () => {
		authClient.signIn
			.email({
				email,
				password,
			})
			.then((e) => {
				if (e.error?.code === "EMAIL_NOT_VERIFIED") {
					headlessToast.error({
						title: "Email not verified",
						description: "Please check your inbox and verify your email to continue.",
					});
					return;
				}
				if (e.error?.code === "INVALID_EMAIL_OR_PASSWORD") {
					headlessToast.error({
						title: "Invalid email or password",
						description: "Please double‑check your login details and try again.",
					});
					return;
				}
				if (e.data) {
					//@ts-expect-error
					if (e.data.twoFactorRedirect) {
						signInEmailTwoFactor();
					} else {
						signInEmail();
					}
				}
			});
	};

	const [sendingReset, setSendingReset] = useState(false);

	// The same message whether or not the email has an account, so the form doesn't reveal who's signed up.
	const handleForgotPassword = async () => {
		setSendingReset(true);
		const result = await authClient.requestPasswordReset({
			email,
			// Absolute: a relative path resolves against the auth server's base URL (the bare root domain in
			// production), where this page doesn't exist.
			redirectTo: `${import.meta.env.VITE_URL_ROOT}/auth/password-reset`,
		});
		setSendingReset(false);
		if (result.error && result.error.status >= 500) {
			headlessToast.error({ title: "Couldn't send the reset email", description: "Please try again in a moment." });
			return;
		}
		headlessToast.success({
			title: "Check your inbox",
			description: `If ${email} has a password on Sayr, we've sent it a link to reset it.`,
		});
	};

	const handleSignUpSubmit = async () => {
		const result = await authClient.signUp.email({
			email,
			password,
			name,
			displayName: name,
		});

		if (result.error) {
			const msg: any = result.error.message;
			let friendly = "Failed to sign up";
			if (msg.includes("Failed to create user")) friendly = "Username already taken";
			if (msg.includes("password")) friendly = "Your password is too weak";

			headlessToast.error({
				title: "Sign up failed",
				description: friendly,
			});

			return;
		}

		headlessToast.success({
			title: "Verify your email",
			description: "We've sent you a verification link. Please check your inbox.",
		});
	};

	const providerButtons = [
		providers?.doras && {
			id: "doras",
			label: "Doras",
			onClick: signInDoras,
			icon: (
				<>
					<img
						src="https://cdn.doras.to/doras/icon-white.svg"
						alt="Doras"
						width={18}
						height={18}
						className="not-dark:hidden"
					/>
					<img
						src="https://cdn.doras.to/doras/icon.svg"
						alt="Doras"
						width={18}
						height={18}
						className="dark:hidden"
					/>
				</>
			),
		},
		providers?.github && {
			id: "github",
			label: "GitHub",
			onClick: signInGithub,
			icon: <IconBrandGithubFilled className="size-[18px]" />,
		},
		providers?.discord && {
			id: "discord",
			label: "Discord",
			onClick: signInDiscord,
			icon: <IconBrandDiscordFilled className="size-[18px]" />,
		},
		providers?.slack && {
			id: "slack",
			label: "Slack",
			onClick: signInSlack,
			icon: <IconBrandSlack className="size-[18px]" />,
		},
		// Not offered on sign up (the grid is hidden there): a passkey can only sign in to an existing account.
		{
			id: "passkey",
			label: "Passkey",
			onClick: handlePasskeySignIn,
			icon: <IconFingerprint className="size-[18px]" />,
		},
	].filter(Boolean) as Array<{
		id: string;
		label: string;
		onClick: () => void;
		icon: React.ReactNode;
	}>;
	const hasAnyProvider = providerButtons.length > 0;

	return (
		<div
			className={cn("rounded-2xl mx-auto w-full max-w-sm", !isDialog && "bg-card border shadow-sm", isDialog && "")}
		>
			{/* Header */}
			<div className="flex flex-col items-center gap-2 px-6 pt-8 pb-6">
				<div className="flex size-16 shrink-0 items-center justify-center mb-1" aria-hidden="true">
					<TasqIcon className="text-primary size-14" />
				</div>
				<h1 className="text-2xl! font-semibold tracking-tight">Sayr.io</h1>
				<p className="text-sm text-muted-foreground">
					{isSignUp ? "Create your account" : "Sign in or create an account"}
				</p>
			</div>

			{/* Body */}
			<div className="px-6 pb-8 flex flex-col gap-3">
				{/* SSO Buttons */}
				{hasAnyProvider && !isSignUp && (
					<div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
						{providerButtons.map((provider, index) => {
							const isLastUsed = lastMethod === provider.id;
							// An odd last button (usually Passkey) spans the row instead of sitting alone in half of it.
							const spansRow = index === providerButtons.length - 1 && providerButtons.length % 2 === 1;
							return (
								<Button
									key={provider.id}
									variant="primary"
									className={cn("w-full justify-start gap-3 h-10 relative", spansRow && "md:col-span-2")}
									onClick={provider.onClick}
								>
									<span className="size-[18px] shrink-0 flex items-center justify-center">
										{provider.icon}
									</span>
									<span className="flex-1 text-left">{provider.label}</span>
									{isLastUsed && (
										<Badge
											variant="secondary"
											className="text-[10px] px-1.5 py-0 h-5 leading-none font-normal shrink-0"
										>
											Last used
										</Badge>
									)}
								</Button>
							);
						})}
					</div>
				)}

				{/* Divider */}
				{hasAnyProvider && !isSignUp && (
					<div className="flex items-center gap-3 my-1">
						<Separator className="flex-1" />
						<span className="text-xs text-muted-foreground whitespace-nowrap">Or continue with email</span>
						<Separator className="flex-1" />
					</div>
				)}

				{/* Email */}
				<div className="flex flex-col gap-2">
					<div className="flex items-end gap-2">
						{step === "email" ? (
							<div className="group relative flex-1">
								<label
									htmlFor="email"
									className="bg-card text-foreground absolute start-1 top-0 z-10 block -translate-y-1/2 px-2 text-xs font-medium group-has-disabled:opacity-50"
								>
									Email address
								</label>
								<Input
									id="email"
									className="h-10 bg-card"
									placeholder="hi@yourcompany.com"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
									autoComplete={"email webauthn"}
								/>
							</div>
						) : step === "name" ? (
							<div className="group relative flex-1">
								<label
									htmlFor="name"
									className="bg-card text-foreground absolute start-1 top-0 z-10 block -translate-y-1/2 px-2 text-xs font-medium group-has-disabled:opacity-50"
								>
									Username
								</label>
								<Input
									id="username"
									className="h-10 bg-card"
									placeholder="username"
									type="text"
									value={name}
									onChange={(e) => {
										const value = e.target.value.toLowerCase();

										// convert spaces to hyphens
										const spaced = value.replace(/ /g, "-");

										// allow only a–z, 0–9, _, -
										const cleaned = spaced.replace(/[^a-z0-9_-]/g, "");

										if (cleaned.length <= 20) {
											setName(cleaned);
										}
									}}
									onKeyDown={(e) => e.key === "Enter" && setStep("password")}
									autoComplete="username"
								/>
							</div>
						) : (
							<div className="group relative flex-1">
								<label
									htmlFor="password"
									className="bg-card text-foreground absolute start-1 top-0 z-10 block -translate-y-1/2 px-2 text-xs font-medium group-has-disabled:opacity-50"
								>
									Password
								</label>
								<Input
									id="password"
									className="h-10 bg-card"
									placeholder="Enter your password"
									type="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									onKeyDown={(e) =>
										e.key === "Enter" && (isSignUp ? handleSignUpSubmit() : handlePasswordSubmit())
									}
									autoComplete="password"
								/>
							</div>
						)}
						<Button
							type="button"
							size="icon"
							className="shrink-0"
							onClick={() => {
								if (step === "email") {
									handleEmailSubmit();
								} else if (step === "name") {
									setStep("password");
								} else {
									isSignUp ? handleSignUpSubmit() : handlePasswordSubmit();
								}
							}}
						>
							<ArrowRight className="size-4" />
						</Button>
					</div>
					{step !== "email" && (
						<div className="flex items-center justify-between gap-2">
							<Button
								variant="link"
								size="sm"
								className="h-auto p-0 text-muted-foreground"
								onClick={() => {
									setStep("email");
									setPassword("");
									setName("");
								}}
							>
								Back to email
							</Button>
							{step === "password" && !isSignUp && (
								<Button
									variant="link"
									size="sm"
									className="h-auto p-0 text-muted-foreground"
									disabled={sendingReset}
									onClick={handleForgotPassword}
								>
									{sendingReset ? "Sending..." : "Forgot password?"}
								</Button>
							)}
						</div>
					)}
					{step === "email" && (
						<Button
							variant="link"
							size="sm"
							className="h-auto p-0 text-muted-foreground self-start"
							onClick={() => {
								setIsSignUp(true);
							}}
						>
							Don't have an account? Sign up
						</Button>
					)}
				</div>
			</div>

			{/* Legal footer */}
			<div className="px-6 pb-6 text-center">
				<p className="text-xs text-muted-foreground">
					By continuing, you agree to our{" "}
					<a
						href="https://sayr.io/legal/terms"
						target="_blank"
						rel="noopener noreferrer"
						className="underline underline-offset-2 hover:text-foreground transition-colors"
					>
						Terms of Service
					</a>{" "}
					and{" "}
					<a
						href="https://sayr.io/legal/privacy"
						target="_blank"
						rel="noopener noreferrer"
						className="underline underline-offset-2 hover:text-foreground transition-colors"
					>
						Privacy Policy
					</a>
					.
				</p>
			</div>
		</div>
	);
}
